import React, { useMemo } from 'react';
import { Text, StyleSheet, View, Platform } from 'react-native';
import { accessibleTextProps } from '../../utils/accessibility-utils';
import {
  fitFontSize,
  fitLineHeight,
} from './welcome-text-utils';
import { WELCOME_MAX_CONTENT_WIDTH } from './welcome-responsive';
import { useWelcomeResponsiveLayout } from './useWelcomeResponsiveLayout';

const LINE_ONE = 'Charge your EV.';
const LINE_TWO = 'Earn from your charger.';

const WelcomeHeadline: React.FC = () => {
  const { textWidth, headlineFit } = useWelcomeResponsiveLayout();

  const fontSize = useMemo(() => {
    const lineOneSize = fitFontSize(LINE_ONE, textWidth, headlineFit);
    const lineTwoSize = fitFontSize(LINE_TWO, textWidth, headlineFit);
    return Math.min(lineOneSize, lineTwoSize);
  }, [textWidth, headlineFit]);

  const lineHeight = fitLineHeight(fontSize, 1.25);
  const baseLineStyle = useMemo(
    () => ({
      fontFamily: 'Poppins-Bold' as const,
      fontSize,
      fontWeight: '700' as const,
      lineHeight,
    }),
    [fontSize, lineHeight]
  );

  const lineTextProps =
    Platform.OS === 'web'
      ? {}
      : {
          numberOfLines: 1 as const,
          adjustsFontSizeToFit: true,
          minimumFontScale: headlineFit.min / headlineFit.max,
        };

  return (
    <View style={styles.wrapper}>
      <Text
        {...accessibleTextProps.body}
        style={[styles.line, baseLineStyle]}
        {...lineTextProps}
      >
        <Text style={[baseLineStyle, styles.yellow]}>Charge</Text>
        <Text style={[baseLineStyle, styles.white]}> your EV.</Text>
      </Text>
      <Text
        {...accessibleTextProps.body}
        style={[styles.line, baseLineStyle]}
        {...lineTextProps}
      >
        <Text style={[baseLineStyle, styles.green]}>Earn</Text>
        <Text style={[baseLineStyle, styles.white]}> from your charger.</Text>
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    maxWidth: WELCOME_MAX_CONTENT_WIDTH,
    alignSelf: 'center',
    alignItems: 'center',
  },
  line: {
    width: '100%',
    textAlign: 'center',
    overflow: 'visible',
  },
  yellow: {
    color: '#FFE54F',
  },
  green: {
    color: '#64E280',
  },
  white: {
    color: '#FFFFFF',
  },
});

export default WelcomeHeadline;
